/**
 * EndExamChoiceModal - Three-option modal for ending an exam.
 *
 * Shown when the instructor triggers End Exam (button click or auto-zero
 * of the header countdown). If any students still have time remaining, the
 * instructor can choose between hard end (cap everyone), soft end (let
 * extra-time students finish via per-student timers), or cancel.
 */
import React from 'react'
import { Modal, Button, Spinner } from 'react-bootstrap'
import { AiOutlineWarning } from 'react-icons/ai'
import httpService from 'services/httpService'
import * as constants from 'Constants'
import styles from '../../LabsV2.module.css'

const parseDurationMs = (str) => {
    if (!str) return 0
    const dayMatch = str.match(/^(\d+)\s+(.+)$/)
    let days = 0
    let rest = str
    if (dayMatch) { days = parseInt(dayMatch[1], 10); rest = dayMatch[2] }
    const parts = rest.split(':')
    if (parts.length !== 3) return 0
    return ((days * 24 + parseInt(parts[0], 10)) * 3600
        + parseInt(parts[1], 10) * 60
        + parseFloat(parts[2])) * 1000
}

export const computeRunningStudents = (students, now = Date.now()) => {
    return (students || [])
        .filter(s => !s.is_finished && s.start_time)
        .map(s => {
            const endMs = s.end_time
                ? new Date(s.end_time).getTime()
                : new Date(s.start_time).getTime() + parseDurationMs(s.duration)
            return {
                email: s.student_email,
                remainingMinutes: Math.max(0, Math.round((endMs - now) / 60000)),
                endMs,
            }
        })
        .filter(s => s.endMs > now)
}

export const fetchRunningStudents = async (labId, now = Date.now()) => {
    try {
        const response = await httpService.get(`${constants.base_url}/exam/${labId}/proctor`)
        const students = response?.data?.student_exam?.results || []
        return computeRunningStudents(students, now)
    } catch {
        return []
    }
}

const EndExamChoiceModal = ({
    show,
    onHide,
    onEndForAll,
    onLetContinue,
    runningStudents = [],
    isLoading = false,
}) => {
    const hasRunning = runningStudents.length > 0

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="lg"
            className={styles.examConfirmModal}
        >
            <Modal.Header closeButton className={styles.examConfirmModalHeader}>
                <Modal.Title className={styles.examConfirmModalTitle}>
                    <AiOutlineWarning size={24} className={styles.examConfirmModalIcon} />
                    End Exam
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className={styles.examConfirmModalBody}>
                {hasRunning ? (
                    <div>
                        <p className={styles.examConfirmModalMessage}>
                            <strong>{runningStudents.length}</strong>{' '}
                            {runningStudents.length === 1 ? 'student still has' : 'students still have'} time
                            remaining. How would you like to end the exam?
                        </p>
                        <ul className="mb-2" style={{ maxHeight: '160px', overflowY: 'auto' }}>
                            {runningStudents.slice(0, 20).map((s) => (
                                <li key={s.email}>
                                    {s.email}{' '}
                                    <small className="text-muted">
                                        ({s.remainingMinutes}m left)
                                    </small>
                                </li>
                            ))}
                            {runningStudents.length > 20 && (
                                <li>
                                    <em>...and {runningStudents.length - 20} more</em>
                                </li>
                            )}
                        </ul>
                        <ul className="mb-0">
                            <li>
                                <strong>End for everyone now</strong> caps all running students immediately.
                            </li>
                            <li>
                                <strong>Let them continue</strong> ends the exam for finished students but
                                lets students with extra time keep working until their personal timer expires.
                            </li>
                        </ul>
                    </div>
                ) : (
                    <p className={styles.examConfirmModalMessage}>
                        No students currently have time remaining. End the exam?
                    </p>
                )}
            </Modal.Body>

            <Modal.Footer className={styles.examConfirmModalFooter}>
                <Button
                    variant="outline-secondary"
                    onClick={onHide}
                    disabled={isLoading}
                    className={styles.examConfirmModalCancelBtn}
                >
                    Cancel
                </Button>
                {hasRunning && (
                    <Button
                        variant="outline-primary"
                        onClick={onLetContinue}
                        disabled={isLoading}
                    >
                        Let them continue
                    </Button>
                )}
                <Button
                    variant="danger"
                    onClick={onEndForAll}
                    disabled={isLoading}
                    className={styles.examConfirmModalConfirmBtn}
                >
                    {isLoading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Processing...
                        </>
                    ) : (
                        'End for everyone now'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default EndExamChoiceModal
